// @ts-nocheck -- TypeScript migration in progress: bulk-renamed from .js and not
// yet typed. Remove this directive and add real types when converting this
// module. Backlog: grep -rl "@ts-nocheck" src. See docs/typescript-migration.md.
import {
  airbornePhaseNames,
  groundPhaseNames,
} from "../core/config.js";
import {
  panelNumber,
} from "../core/display.js";
import {
  formatElapsedTime,
} from "../core/format.js";
import {
  averageValues,
  extentFromValues,
} from "../core/math.js";
import {
  rawMetricDescriptor,
} from "../core/raw-metadata.js";
import {
  engineWorkPoint as defaultEngineWorkPoint,
  fuelUsedMetric as defaultFuelUsedMetric,
  powerEstimateSummary as defaultPowerEstimateSummary,
} from "../analysis/operating-context.js";
import {
  engineMetricValue,
} from "../core/engines.js";
import {
  median,
} from "../analysis/utils.js";

function projectPointsForEngine(points, engine) {
  return points.map((point) => {
    const fuelFlow = engineMetricValue(point, engine, "fuelFlow");
    const fuelUsed = engineMetricValue(point, engine, "fuelUsed");
    const rpm = engineMetricValue(point, engine, "rpm");
    return {
      ...point,
      fuelFlow: Number.isFinite(fuelFlow) ? fuelFlow : undefined,
      fuelUsed: Number.isFinite(fuelUsed) ? fuelUsed : undefined,
      rpm: Number.isFinite(rpm) ? rpm : undefined,
    };
  });
}

function projectPointsAcrossEngines(points, engines) {
  return points.map((point) => {
    const fuelFlows = engines.map((engine) => engineMetricValue(point, engine, "fuelFlow")).filter(Number.isFinite);
    const fuelUseds = engines.map((engine) => engineMetricValue(point, engine, "fuelUsed")).filter(Number.isFinite);
    return {
      ...point,
      fuelFlow: fuelFlows.length === engines.length ? fuelFlows.reduce((total, value) => total + value, 0) : point.fuelFlow,
      fuelUsed: fuelUseds.length === engines.length ? fuelUseds.reduce((total, value) => total + value, 0) : point.fuelUsed,
    };
  });
}

function defaultFormatPointTime(point) {
  if (point?.displayTime) return point.displayTime;
  if (point?.timestamp) return String(point.timestamp);
  return "--";
}

function phaseChannelCoverage(points, key) {
  return points.map((point) => point?.[key]).filter(Number.isFinite).length / Math.max(points.length, 1);
}

function bestSpeedKey(data) {
  return ["ias", "tas", "groundSpeed"].find((key) => data.some((point) => Number.isFinite(point[key]))) ?? null;
}

function degreesToRadians(value) {
  return value * Math.PI / 180;
}

function distanceNmBetweenPoints(start, end) {
  if (![start?.latitude, start?.longitude, end?.latitude, end?.longitude].every(Number.isFinite)) return null;
  const earthRadiusNm = 3440.065;
  const lat1 = degreesToRadians(start.latitude);
  const lat2 = degreesToRadians(end.latitude);
  const deltaLat = degreesToRadians(end.latitude - start.latitude);
  const deltaLon = degreesToRadians(end.longitude - start.longitude);
  const a = Math.sin(deltaLat / 2) ** 2
    + Math.cos(lat1) * Math.cos(lat2) * Math.sin(deltaLon / 2) ** 2;
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return earthRadiusNm * c;
}

export function phaseProfileRows(data = []) {
  if (!data.length) return [];
  const rows = [];
  let start = 0;
  for (let index = 1; index <= data.length; index += 1) {
    if (index < data.length && data[index].phase === data[start].phase) continue;
    const points = data.slice(start, index);
    const first = points[0];
    const last = points.at(-1);
    const startSeconds = first?.elapsedSeconds ?? start;
    const endSeconds = last?.elapsedSeconds ?? index - 1;
    rows.push({
      name: first?.phase || "Unknown",
      startSeconds,
      endSeconds,
      durationSeconds: Math.max(0, endSeconds - startSeconds),
    });
    start = index;
  }
  return rows;
}

export function profileBucketKey(phaseName) {
  if (groundPhaseNames.has(phaseName)) return "ground";
  if (airbornePhaseNames.has(phaseName)) return "airborne";
  if (phaseName === "Shutdown") return "off";
  return "unknown";
}

export function profileDurationBuckets(phases = []) {
  const bucketDefinitions = [
    ["ground", "Ground / run-up"],
    ["airborne", "Airborne"],
    ["off", "Shutdown / off"],
    ["unknown", "Unknown"],
  ];
  const totals = new Map(bucketDefinitions.map(([key]) => [key, 0]));
  phases.forEach((phase) => {
    const duration = Number.isFinite(phase.durationSeconds) ? Math.max(0, phase.durationSeconds) : 0;
    if (!duration) return;
    const key = profileBucketKey(phase.name);
    totals.set(key, (totals.get(key) ?? 0) + duration);
  });
  const total = [...totals.values()].reduce((sum, value) => sum + value, 0) || 1;
  return bucketDefinitions
    .map(([key, label]) => ({
      key,
      label,
      durationSeconds: totals.get(key) ?? 0,
      percent: (totals.get(key) ?? 0) / total * 100,
    }))
    .filter((bucket) => bucket.durationSeconds > 0);
}

export function dominantProfileValue(data, key, fallback = "Not labeled") {
  const counts = new Map();
  data.forEach((point) => {
    const value = String(point?.[key] ?? "").trim();
    if (!value) return;
    counts.set(value, (counts.get(value) ?? 0) + 1);
  });
  return [...counts.entries()].sort((a, b) => b[1] - a[1])[0]?.[0] ?? fallback;
}

export function humanizeProfileSource(value) {
  const clean = String(value ?? "").trim();
  if (!clean || clean === "Not labeled") return "Not labeled";
  if (/air\/gps/i.test(clean)) return "Air/GPS + engine";
  if (/ground-contact/i.test(clean)) return "Ground-contact + engine";
  if (/engine data only/i.test(clean)) return "Engine data only";
  if (/thermal trend/i.test(clean)) return "Thermal trend";
  return clean.replace(/[-_]/g, " ").replace(/\b\w/g, (letter) => letter.toUpperCase());
}

export function profileChannelSummary(data, channels, emptyLabel) {
  const labels = channels
    .filter(([key]) => key && phaseChannelCoverage(data, key) >= 0.25)
    .map(([, label]) => label);
  return labels.length ? labels.join(" + ") : emptyLabel;
}

export function formatProfileDuration(seconds) {
  return Number.isFinite(seconds) && seconds > 0 ? formatElapsedTime(seconds) : "Not detected";
}

export function profileShareLabel(seconds, totalSeconds) {
  if (!Number.isFinite(seconds) || seconds <= 0 || !Number.isFinite(totalSeconds) || totalSeconds <= 0) return "0% of log";
  return `${Math.round(seconds / totalSeconds * 100)}% of log`;
}

export function flightProfileContext(phases = [], data = [], {
  flightDurationSeconds = () => 0,
} = {}) {
  const buckets = profileDurationBuckets(phases);
  const bucketDuration = (key) => buckets.find((bucket) => bucket.key === key)?.durationSeconds ?? 0;
  const totalSeconds = buckets.reduce((sum, bucket) => sum + bucket.durationSeconds, 0) || flightDurationSeconds() || 0;
  const speedKey = bestSpeedKey(data);
  const speedLabel = speedKey ? rawMetricDescriptor(speedKey)[0] : "Speed";
  const motionLabel = profileChannelSummary(data, [
    ["altitude", "Altitude"],
    [speedKey, speedLabel],
    ["verticalSpeed", "Vertical speed"],
  ], "No air/GPS channels");
  const engineLabel = profileChannelSummary(data, [
    ["rpm", "RPM"],
    ["map", "MAP"],
    ["fuelFlow", "Fuel flow"],
    ["buss", "Bus volts"],
  ], "Sparse engine context");
  const modeNames = new Set(phases.map((phase) => phase.name).filter((name) => name && name !== "Unknown"));
  return {
    buckets,
    totalSeconds,
    groundSeconds: bucketDuration("ground"),
    airborneSeconds: bucketDuration("airborne"),
    offSeconds: bucketDuration("off"),
    source: humanizeProfileSource(dominantProfileValue(data, "phaseSource")),
    confidence: dominantProfileValue(data, "phaseConfidence", "Unstated"),
    motionLabel,
    motionDetail: motionLabel === "No air/GPS channels" ? "Airborne timing is inferred from engine traces" : "Supports altitude and speed context",
    engineLabel,
    segmentCount: phases.length,
    modeCount: modeNames.size,
  };
}

export function operationalProfileCards(profile) {
  const detailCount = profile.segmentCount === 1
    ? "Detected segment feeds filters and scoring"
    : `${profile.segmentCount.toLocaleString()} detected segments feed filters and scoring`;
  return [
    { label: "Ground / run-up", value: formatProfileDuration(profile.groundSeconds), detail: profileShareLabel(profile.groundSeconds, profile.totalSeconds) },
    { label: "Airborne", value: formatProfileDuration(profile.airborneSeconds), detail: profileShareLabel(profile.airborneSeconds, profile.totalSeconds) },
    { label: "Profile basis", value: profile.source, detail: `${profile.confidence} confidence most often` },
    { label: "Motion channels", value: profile.motionLabel, detail: profile.motionDetail },
    { label: "Engine context", value: profile.engineLabel, detail: "Used for power and health review" },
    { label: "Phase detail", value: "Summarized", detail: detailCount },
  ];
}

export function operationalProfileReportRows(phases = [], data = [], options = {}) {
  return operationalProfileCards(flightProfileContext(phases, data, options))
    .map((card) => [card.label, [card.value, card.detail].filter(Boolean).join(" - ")]);
}

export function averageForProfileKey(data, key) {
  return averageValues(data.map((point) => point[key]).filter(Number.isFinite));
}

export function maxForProfileKey(data, key) {
  return extentFromValues(data.map((point) => point[key]).filter(Number.isFinite))?.max ?? null;
}

export function finiteProfileValues(data, key) {
  return data.map((point) => point[key]).filter(Number.isFinite);
}

export function maxPositiveForProfileKey(data, key) {
  const values = finiteProfileValues(data, key).filter((value) => value > 0);
  return extentFromValues(values)?.max ?? null;
}

export function minNegativeForProfileKey(data, key) {
  const values = finiteProfileValues(data, key).filter((value) => value < 0);
  return extentFromValues(values)?.min ?? null;
}

export function densityAltitudeAtTakeoffForProfile(data = []) {
  const takeoffValues = data
    .filter((point) => point?.phase === "Takeoff")
    .map((point) => point.densityAltitude)
    .filter(Number.isFinite);
  return median(takeoffValues);
}

export function altitudeGainForProfile(data) {
  const extent = extentFromValues(finiteProfileValues(data, "altitude"));
  if (!extent) return null;
  const gain = extent.max - extent.min;
  return gain > 0 ? gain : null;
}

export function distanceNmForProfile(data = []) {
  let gpsDistance = 0;
  let hasGpsInterval = false;
  for (let index = 1; index < data.length; index += 1) {
    const segmentDistance = distanceNmBetweenPoints(data[index - 1], data[index]);
    if (!Number.isFinite(segmentDistance)) continue;
    gpsDistance += segmentDistance;
    hasGpsInterval = true;
  }
  if (hasGpsInterval) return gpsDistance;

  let speedDistance = 0;
  let hasSpeedInterval = false;
  for (let index = 1; index < data.length; index += 1) {
    const previous = data[index - 1];
    const current = data[index];
    const startSeconds = previous?.elapsedSeconds;
    const endSeconds = current?.elapsedSeconds;
    const startSpeed = previous?.groundSpeed;
    const endSpeed = current?.groundSpeed;
    if (![startSeconds, endSeconds, startSpeed, endSpeed].every(Number.isFinite)) continue;
    const deltaSeconds = endSeconds - startSeconds;
    if (!(deltaSeconds > 0)) continue;
    speedDistance += ((startSpeed + endSpeed) / 2) * (deltaSeconds / 3600);
    hasSpeedInterval = true;
  }
  return hasSpeedInterval ? speedDistance : null;
}

export function formatProfileDistance(distanceNm) {
  if (!Number.isFinite(distanceNm) || distanceNm <= 0) return "n/a";
  return `${distanceNm >= 100 ? Math.round(distanceNm).toLocaleString() : distanceNm.toFixed(1)} NM`;
}

export function flightProfileAirDataTiles(data, {
  number = panelNumber,
} = {}) {
  const tiles = [];
  const takeoffDensityAltitude = densityAltitudeAtTakeoffForProfile(data);
  if (Number.isFinite(takeoffDensityAltitude)) {
    tiles.push(["Density Alt at Takeoff", number(takeoffDensityAltitude, " ft")]);
  }
  const altitudeGain = altitudeGainForProfile(data);
  if (Number.isFinite(altitudeGain) && altitudeGain >= 100) {
    tiles.push(["Altitude gain", number(altitudeGain, " ft")]);
  }
  [
    ["ias", "Max indicated airspeed"],
    ["tas", "Max true airspeed"],
    ["groundSpeed", "Max ground speed"],
  ].forEach(([key, label]) => {
    const value = maxPositiveForProfileKey(data, key);
    if (Number.isFinite(value)) tiles.push([label, number(value, " kt")]);
  });
  const averageClimb = averageValues(finiteProfileValues(data, "verticalSpeed").filter((value) => value > 0));
  if (Number.isFinite(averageClimb)) tiles.push(["Avg climb rate", number(averageClimb, " fpm")]);
  const maxClimb = maxPositiveForProfileKey(data, "verticalSpeed");
  if (Number.isFinite(maxClimb)) tiles.push(["Max climb rate", number(maxClimb, " fpm")]);
  const maxDescent = minNegativeForProfileKey(data, "verticalSpeed");
  if (Number.isFinite(maxDescent)) tiles.push(["Max descent rate", number(Math.abs(maxDescent), " fpm")]);
  const oat = averageForProfileKey(data, "oat");
  if (Number.isFinite(oat)) tiles.push(["Avg OAT", number(oat, " F")]);
  return tiles;
}

export function flightProfileSummaryItems(data = [], phases = phaseProfileRows(data), {
  engines = [],
  engineWorkPoint = defaultEngineWorkPoint,
  flightDurationSeconds = () => 0,
  formatPointTime = defaultFormatPointTime,
  fuelUsedMetric = defaultFuelUsedMetric,
  number = panelNumber,
  powerEstimateSummary = defaultPowerEstimateSummary,
} = {}) {
  const first = data[0];
  const last = data.at(-1);
  const elapsedExtent = extentFromValues(data.map((point) => point.elapsedSeconds).filter(Number.isFinite));
  const durationSeconds = elapsedExtent ? Math.max(0, elapsedExtent.max - elapsedExtent.min) : Math.max(0, data.length - 1);
  const profile = flightProfileContext(phases, data, { flightDurationSeconds });
  const airborneData = data.filter((point) => airbornePhaseNames.has(point?.phase));
  const cruiseData = data.filter((point) => point?.phase === "Cruise");
  const workData = data.filter(engineWorkPoint);
  const distanceNm = distanceNmForProfile(data);
  const fuelFlow = averageForProfileKey(workData, "fuelFlow");
  const cruiseFuelFlow = averageForProfileKey(cruiseData, "fuelFlow");
  const averageGroundSpeed = averageForProfileKey(airborneData, "groundSpeed");
  const amps = averageForProfileKey(workData, "amps");
  const rpm = averageForProfileKey(workData, "rpm");
  const bus = median(workData.map((point) => point.buss).filter(Number.isFinite));
  const powerSummary = powerEstimateSummary(data, "work", { engineWorkPoint });
  const fuelConsumed = fuelUsedMetric(data);
  const multiEngine = Array.isArray(engines) && engines.length > 1;
  const headerItems = [
    ["Time range", first && last ? `${formatPointTime(first)} - ${formatPointTime(last)}` : "n/a"],
    ["Duration", formatElapsedTime(durationSeconds)],
    ["Samples", data.length.toLocaleString()],
    ["Airborne time", formatProfileDuration(profile.airborneSeconds)],
    ...(Number.isFinite(distanceNm) ? [["Distance travelled", formatProfileDistance(distanceNm)]] : []),
    ["Max altitude", number(maxForProfileKey(data, "altitude"), " ft")],
    ...flightProfileAirDataTiles(data, { number }),
    ...(Number.isFinite(averageGroundSpeed) ? [["Avg ground speed", number(averageGroundSpeed, " kt")]] : []),
  ];
  if (!multiEngine) {
    return [
      ...headerItems,
      ...(fuelConsumed !== "Not logged" ? [["Fuel consumed", fuelConsumed]] : []),
      ["Avg fuel flow", number(fuelFlow, " GPH", 1)],
      ...(Number.isFinite(cruiseFuelFlow) ? [["Avg cruise fuel flow", number(cruiseFuelFlow, " GPH", 1)]] : []),
      ...(Number.isFinite(amps) ? [["Avg amps", number(amps, " A", 1)]] : []),
      ["Avg RPM", number(rpm)],
      ["Avg HP", number(powerSummary?.avgHp, " hp")],
      ["Peak HP", number(powerSummary?.peakHp, " hp")],
      ["Avg torque", number(powerSummary?.avgTorque, " lb-ft")],
      ["Peak torque", number(powerSummary?.peakTorque, " lb-ft")],
      ["Running bus", number(bus, " V", 1)],
    ];
  }

  const aircraftFuelConsumed = fuelConsumed !== "Not logged"
    ? fuelConsumed
    : fuelUsedMetric(projectPointsAcrossEngines(data, engines));
  const sharedItems = [
    ...(aircraftFuelConsumed && aircraftFuelConsumed !== "Not logged" ? [["Total fuel consumed", aircraftFuelConsumed, "shared"]] : []),
    ...(Number.isFinite(amps) ? [["Avg amps", number(amps, " A", 1), "shared"]] : []),
    ["Running bus", number(bus, " V", 1), "shared"],
  ];

  const perEngineItems = [];
  engines.forEach((engine) => {
    const projectedAll = projectPointsForEngine(data, engine);
    const projectedWork = projectPointsForEngine(workData, engine);
    const projectedCruise = projectPointsForEngine(cruiseData, engine);
    const engineFuelConsumed = fuelUsedMetric(projectedAll);
    const engineFuelFlow = averageForProfileKey(projectedWork, "fuelFlow");
    const engineCruiseFuelFlow = averageForProfileKey(projectedCruise, "fuelFlow");
    const engineRpm = averageForProfileKey(projectedWork, "rpm");
    const enginePower = powerEstimateSummary(projectedAll, "work", { engineWorkPoint });
    const subgroup = engine?.id ?? engine?.engineKey;
    if (engineFuelConsumed && engineFuelConsumed !== "Not logged") {
      perEngineItems.push(["Fuel consumed", engineFuelConsumed, subgroup]);
    }
    if (Number.isFinite(engineFuelFlow)) {
      perEngineItems.push(["Avg fuel flow", number(engineFuelFlow, " GPH", 1), subgroup]);
    }
    if (Number.isFinite(engineCruiseFuelFlow)) {
      perEngineItems.push(["Avg cruise fuel flow", number(engineCruiseFuelFlow, " GPH", 1), subgroup]);
    }
    if (Number.isFinite(engineRpm)) {
      perEngineItems.push(["Avg RPM", number(engineRpm), subgroup]);
    }
    if (Number.isFinite(enginePower?.avgHp)) {
      perEngineItems.push(["Avg HP", number(enginePower.avgHp, " hp"), subgroup]);
    }
    if (Number.isFinite(enginePower?.peakHp)) {
      perEngineItems.push(["Peak HP", number(enginePower.peakHp, " hp"), subgroup]);
    }
    if (Number.isFinite(enginePower?.avgTorque)) {
      perEngineItems.push(["Avg torque", number(enginePower.avgTorque, " lb-ft"), subgroup]);
    }
    if (Number.isFinite(enginePower?.peakTorque)) {
      perEngineItems.push(["Peak torque", number(enginePower.peakTorque, " lb-ft"), subgroup]);
    }
  });

  return [...headerItems, ...sharedItems, ...perEngineItems];
}

export function groupFlightProfileSummaryItems(items = []) {
  const sectionDefinitions = [
    {
      key: "overview",
      title: "Flight overview",
      labels: new Set(["Time range", "Duration", "Samples", "Airborne time", "Distance travelled"]),
    },
    {
      key: "air",
      title: "Air and altitude",
      labels: new Set([
        "Max altitude",
        "Altitude gain",
        "Avg ground speed",
        "Avg climb rate",
        "Max indicated airspeed",
        "Max true airspeed",
        "Max ground speed",
        "Max climb rate",
        "Max descent rate",
        "Avg OAT",
        "Density Alt at Takeoff",
      ]),
    },
    {
      key: "engine",
      title: "Engine and electrical",
      labels: new Set([
        "Total fuel consumed",
        "Fuel consumed",
        "Avg fuel flow",
        "Avg cruise fuel flow",
        "Avg amps",
        "Avg RPM",
        "Avg HP",
        "Peak HP",
        "Avg torque",
        "Peak torque",
        "Running bus",
      ]),
    },
  ];
  const sections = sectionDefinitions.map((section) => ({
    key: section.key,
    title: section.title,
    items: [],
  }));
  const otherSection = {
    key: "other",
    title: "Other context",
    items: [],
  };
  items.forEach((item) => {
    const [label] = item;
    const section = sections.find((candidate, index) => sectionDefinitions[index].labels.has(label));
    if (section) {
      section.items.push(item);
      return;
    }
    otherSection.items.push(item);
  });
  const result = [...sections, otherSection].filter((section) => section.items.length > 0);
  result.forEach((section) => {
    const groups = new Map();
    section.items.forEach((item) => {
      const key = item[2] ?? "_default";
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(item);
    });
    section.subgroups = [...groups.entries()].map(([key, subItems]) => ({ key, items: subItems }));
  });
  return result;
}
